import type { ReactNode } from 'react';
import { useDocumentTitle } from '@/hooks/use-document-title';

interface PageHeaderProps {
  title: string;
  /** Muted one-liner under the title row */
  description?: string;
  /** Right-aligned slot for page-level buttons */
  actions?: ReactNode;
  children?: ReactNode;
  /**
   * Browser-tab title; defaults to `title`. Pass an explicit string to diverge,
   * or `false` to opt out (pages whose tab title depends on async data, e.g.
   * broker-detail, call useDocumentTitle themselves).
   */
  documentTitle?: string | false;
}

/**
 * Canonical page title row: one h1 scale portal-wide (text-3xl —
 * the deliberate pick over the drifted text-2xl minority) plus tab-title wiring.
 */
export const PageHeader = ({ title, description, actions, children, documentTitle }: PageHeaderProps) => {
  useDocumentTitle(documentTitle === false ? undefined : (documentTitle ?? title));

  return (
    <header className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-semibold">{title}</h1>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
      {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      {children}
    </header>
  );
};
PageHeader.displayName = 'PageHeader';
